import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatContextualMenu } from "blocks-nextjs/blocks-react/components/ContextualMenu/ContextualMenu";
import { CatIconCalendarClock } from "blocks-nextjs/blocks-react/components/Icons/CalendarClock/CalendarClock";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconCopy } from "blocks-nextjs/blocks-react/components/Icons/Copy/Copy";
import { CatIconEdit } from "blocks-nextjs/blocks-react/components/Icons/Edit/Edit";
import { CatIconEllipsis } from "blocks-nextjs/blocks-react/components/Icons/Ellipsis/Ellipsis";
import { CatIconShow } from "blocks-nextjs/blocks-react/components/Icons/Show/Show";
import { CatIconSyncronize } from "blocks-nextjs/blocks-react/components/Icons/Syncronize/Syncronize";
import { CatIconWarningTriangleFilled } from "blocks-nextjs/blocks-react/components/Icons/WarningTriangleFilled/WarningTriangleFilled";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatPagination } from "blocks-nextjs/blocks-react/components/Pagination/Pagination";
import { CatStatusIndicator } from "blocks-nextjs/blocks-react/components/StatusIndicator/StatusIndicator";
import { CatTooltip } from "blocks-nextjs/blocks-react/components/Tooltip/Tooltip";
import React, { useEffect, useMemo, useState } from "react";
import ErrorChargingOption from "../../assets/ErrorChargingOption.png";
import Frame from "../../assets/Frame 627345.svg";
import { useChargerAssetLinks } from "../../services/query/eflow-charger-asset-links.query";
import { getDateRangeFromSelection } from "../../shared/dateRangeHelper";
import { isCatAdminUserEnergyFlowLinks } from "../../shared/validateUserPermission";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import useSharedLinksStore from "../../store/useSharedLinksStore";
import { formatDateWithTimezoneOffset, parseDateString } from "../../utils/dateFormatter";
import APIError from "../APIError/APIError";
import DeactivateLinkModal from "./DeactivateLinkModal";
import EditLinkModal from "./EditLinkModal";
import SharedLinkToast from "./SharedLinkToast";
const CONTEXT_LINKS_MANAGEMENT = "links-management";
const CONTEXT_SHARED_LINKS_LIST = "shared-links-list";
const DEFAULT_EMPTY_MESSAGE = "No records found";
const PAGE_SIZE_MANAGEMENT = 3;
const PAGE_SIZE_LIST = 5;
const STATUS_ACTIVE = "Active";
const STATUS_DEACTIVATED = "Deactivated";
const STATUS_EXPIRED = "Expired";
const SORT_DEFAULT_PRIORITY = 999;
const TABLE_COL_SPAN = 8;
const STATUS_PRIORITY = {
    [STATUS_ACTIVE]: 1,
    [STATUS_DEACTIVATED]: 2,
    [STATUS_EXPIRED]: 3
};
const getStatusVariant = (status) => {
    switch (status.toUpperCase()) {
        case "ACTIVE":
            return "active";
        case "DEACTIVATED":
            return "in-determined";
        case "EXPIRED":
            return "error";
        default:
            return "in-determined";
    }
};
const formatStatusForDisplay = (status) => {
    if (!status)
        return "";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};
const isInactiveStatus = (status) => {
    const s = status.toLowerCase();
    return s === "expired" || s === "deactivated";
};
const SharedList = ({ context = "shared-links-list", customEndDate, customStartDate, dateRange = "Last7Days", dateRangeSelectionCounter = 0, emptyMessage = DEFAULT_EMPTY_MESSAGE, forceEmpty = false, isDrawerActive = true, partyNumber, refetchKey, userId }) => {
    const [showCopyToast, setShowCopyToast] = useState(false);
    const [showEditToast, setShowEditToast] = useState(false);
    const [showDeactivateToast, setShowDeactivateToast] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [deactivatingLink, setDeactivatingLink] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    // For links-management: show only 3 items without pagination
    // For shared-links-list: show 5 items with pagination
    const [pageSize, setPageSize] = useState(context === CONTEXT_LINKS_MANAGEMENT ? PAGE_SIZE_MANAGEMENT : PAGE_SIZE_LIST);
    const [cursor, setCursor] = useState(""); // Cursor for pagination
    const [skip, setSkip] = useState(0); // Number of records to skip
    // Track cursors for each page to support backward navigation
    const [pageCursors, setPageCursors] = useState({});
    // Page size options for pagination dropdown
    const pageSizeOptions = [5, 10, 20];
    // Client-side sorting - default to expiryDate DESC (newest links first)
    const [sortBy, setSortBy] = useState("expiryDate");
    const [orderBy, setOrderBy] = useState("DESC");
    // Reset pagination when refetchKey changes (triggered by new link creation)
    React.useEffect(() => {
        if (refetchKey) {
            setCurrentPage(1);
            setCursor("");
            setSkip(0);
            setPageCursors({});
        }
    }, [refetchKey]);
    // Reset pagination when date filter changes
    React.useEffect(() => {
        // For Custom Date: only reset when actual custom dates are provided
        // Don't reset when just selecting "Custom Date" from dropdown
        if (dateRange === "CustomDate") {
            // Only reset if both custom dates are provided (user actually searched)
            if (customStartDate && customEndDate) {
                setCurrentPage(1);
                setCursor("");
                setSkip(0);
                setPageCursors({});
            }
        }
        else {
            // For preset date ranges (Last7Days, Last30Days, etc.), reset immediately
            setCurrentPage(1);
            setCursor("");
            setSkip(0);
            setPageCursors({});
        }
    }, [dateRange, customStartDate, customEndDate]);
    const { links, setLinks, updateLink } = useSharedLinksStore((state) => ({
        links: state.links,
        setLinks: state.setLinks,
        updateLink: state.updateLink
    }));
    const { userEntitlements, userPartyNumber } = useGlobalStore((state) => ({
        userEntitlements: state.userEntitlements,
        userPartyNumber: state.userPartyNumber
    }));
    const { assetDetails, projectMetaData } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData
    }));
    const effectivePartyNumber = partyNumber || userPartyNumber || "";
    const effectiveUserId = userId || "";
    const projectId = projectMetaData?.projectId;
    const assetId = assetDetails?.assetId;
    // Get the project's timezone offset for conversion (matching history/transactions pattern)
    const timezoneOffset = projectMetaData?.offset || "UTC+00:00";
    // Calculate date range based on selection using project's timezone
    const { endDate, startDate } = useMemo(() => {
        return getDateRangeFromSelection(dateRange, customStartDate, customEndDate, timezoneOffset);
    }, [customEndDate, customStartDate, dateRange, timezoneOffset]);
    // Memoize request body to prevent unnecessary re-renders and API calls
    // Include dateRange in dependencies to force refetch when user re-selects the same date range
    // Include dateRangeSelectionCounter to force refetch on every date range selection
    const requestBody = useMemo(() => ({
        assetId,
        filters: [
            {
                includeLeft: true,
                includeRight: true,
                propertyName: "timestamp",
                range: {
                    // Format dates with timezone offset (matching history/transactions pattern)
                    // Send dates as-is with time portion (00:00:00 for start, 23:59:59.999 for end)
                    left: formatDateWithTimezoneOffset(startDate, timezoneOffset),
                    right: formatDateWithTimezoneOffset(endDate, timezoneOffset)
                },
                type: "timeRange"
            }
        ],
        logicalExpression: "$0",
        partyNumber: effectivePartyNumber,
        projectId,
        userId: effectiveUserId
    }), [
        assetId,
        effectivePartyNumber,
        projectId,
        effectiveUserId,
        startDate,
        endDate,
        timezoneOffset,
        dateRange,
        dateRangeSelectionCounter
    ]);
    // Determine if we should sort on frontend (for status) or backend (for other fields)
    const isFrontendSort = sortBy === "status";
    // For frontend sort, use expiryDate ASC as default for API
    // For backend sort, use the actual sortBy and orderBy values
    const apiSortField = isFrontendSort ? "expiryDate" : sortBy;
    const apiOrderBy = isFrontendSort ? "ASC" : orderBy;
    // Check if all required data is available for the query
    const isQueryEnabled = isDrawerActive &&
        !forceEmpty &&
        !!projectId &&
        !!assetId &&
        !!effectivePartyNumber &&
        effectivePartyNumber.trim() !== "";
    // Fetch charger asset links
    const { data: chargerLinksData, isError, isFetching, isLoading, refetch } = useChargerAssetLinks(requestBody, cursor || undefined, // cursor for pagination
    pageSize, // limit - number of items per page
    skip, // skip - number of records to skip for pagination
    [apiOrderBy], // orderBy - ASC or DESC
    [apiSortField], // sort field
    isQueryEnabled, // enabled only when we have required data
    refetchKey // refetch key to force re-fetching when drawer reopens or new link is created
    );
    // Get nextCursor from the response metadata
    const nextCursor = chargerLinksData?.responseMetaData?.nextCursor;
    // Store the cursor for the next page when we receive a new nextCursor
    useEffect(() => {
        if (nextCursor && currentPage) {
            setPageCursors((prev) => ({
                ...prev,
                [currentPage + 1]: nextCursor
            }));
        }
    }, [nextCursor, currentPage]);
    // Calculate total records estimate
    // Since API doesn't provide totalCount, we estimate based on current data
    // This is a limitation of the current API - we can only show basic pagination
    const totalRecords = useMemo(() => {
        const currentRecords = chargerLinksData?.chargerLinks?.length || 0;
        // If we have a nextCursor, there are more pages beyond the current one
        if (nextCursor) {
            // Estimate: Show enough records to indicate more pages exist
            return (currentPage + 1) * pageSize;
        }
        // No nextCursor means this is (or should be) the last page
        // Calculate what totalRecords should be to show we're on the last page
        const calculatedTotal = skip + currentRecords;
        // If we're on a page beyond page 1 and have no records,
        // it means we navigated too far - cap totalRecords at the current page's upper bound
        if (currentRecords === 0 && currentPage > 1) {
            // To disable the Next button in CatPagination:
            // totalRecords should be <= currentPage * pageSize
            // We want to show we're at the boundary, so return currentPage * pageSize
            // This makes the pagination show we're exactly at the end
            return currentPage * pageSize;
        }
        return calculatedTotal;
    }, [chargerLinksData, currentPage, pageSize, skip, nextCursor]);
    // Transform API response
    useEffect(() => {
        // Clear links on error (e.g., 401 unauthorized)
        if (isError) {
            setLinks([]);
            return;
        }
        if (chargerLinksData?.chargerLinks) {
            const currentPageData = chargerLinksData.chargerLinks;
            const transformedLinks = currentPageData.map((link) => {
                // Convert uppercase status to title case (ACTIVE -> Active, DEACTIVATED -> Deactivated)
                const formatStatus = (status, expiryDate) => {
                    let formattedStatus = "";
                    if (status) {
                        formattedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                    }
                    // If status is Deactivated, return as is
                    if (formattedStatus === "Deactivated") {
                        return formattedStatus;
                    }
                    // Check expiry date against today's date
                    if (expiryDate) {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
                        const expiry = parseDateString(expiryDate);
                        // If parsing fails, treat as invalid/expired for safety
                        if (!expiry) {
                            return "Expired";
                        }
                        // If expiry date is greater than or equal to today, status is Active, otherwise Expired
                        return expiry >= today ? "Active" : "Expired";
                    }
                    // Fallback to formatted status if no expiry date (could be empty if status was empty)
                    return formattedStatus;
                };
                const linkExpiryDate = link.expiryDate || "";
                return {
                    expiryDate: linkExpiryDate,
                    features: {
                        canSeeHistory: link.features?.includes("can see history") || false,
                        canUseChargerControls: link.features?.includes("can use charger controls") || false,
                        canUseSchedule: link.features?.includes("can use schedule") || false,
                        liveViewOnly: link.features?.includes("live view only") || false
                    },
                    link: link.link || "",
                    name: link.name || "",
                    status: formatStatus(link.status || "", linkExpiryDate),
                    views: link.views || 0
                };
            });
            if (sortBy === "status") {
                transformedLinks.sort((a, b) => {
                    const priorityA = STATUS_PRIORITY[a.status] ?? SORT_DEFAULT_PRIORITY;
                    const priorityB = STATUS_PRIORITY[b.status] ?? SORT_DEFAULT_PRIORITY;
                    return orderBy === "ASC" ? priorityA - priorityB : priorityB - priorityA;
                });
            }
            setLinks(transformedLinks);
        }
    }, [chargerLinksData, pageSize, setLinks, sortBy, orderBy, isError]);
    const handleEditLink = (link) => {
        if (isInactiveStatus(link.status))
            return;
        setEditingLink(link);
    };
    const handleDeactivateLink = (link) => {
        if (isInactiveStatus(link.status))
            return;
        setDeactivatingLink(link);
    };
    const handleConfirmDeactivate = () => {
        if (deactivatingLink) {
            updateLink(deactivatingLink.link, { status: STATUS_DEACTIVATED });
            setDeactivatingLink(null);
            setShowDeactivateToast(true);
        }
    };
    const handleSaveLink = (updatedLink) => {
        updateLink(updatedLink.link, updatedLink);
        setEditingLink(null);
        setShowEditToast(true);
    };
    // Handle page change from CatPagination component
    const onPageChange = (event) => {
        const newPageSize = event.detail.pageSize;
        const newPageNumber = event.detail.pageNumber;
        // If page size changed, reset to first page and clear all cursors
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
            setCursor("");
            setSkip(0);
            setPageCursors({});
        }
        else {
            // Page number changed
            setCurrentPage(newPageNumber);
            // Calculate skip value based on page number
            const newSkip = (newPageNumber - 1) * pageSize;
            setSkip(newSkip);
            // Use stored cursor for this page if available, otherwise empty string for page 1
            const pageSpecificCursor = newPageNumber === 1 ? "" : pageCursors[newPageNumber] || "";
            setCursor(pageSpecificCursor);
        }
    };
    const handleCopyLink = React.useCallback(async (link) => {
        try {
            if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
            }
            setShowCopyToast(true);
        }
        catch (error) {
            // Handle error silently or show error toast if needed
            console.error("Failed to copy link:", error);
        }
    }, []);
    const handleSort = (field) => {
        if (sortBy === field) {
            // Toggle order if clicking on the same field
            setOrderBy(orderBy === "ASC" ? "DESC" : "ASC");
        }
        else {
            // Set new field and default to ASC
            setSortBy(field);
            setOrderBy("ASC");
        }
    };
    const getSortIcon = React.useCallback((field) => {
        if (sortBy !== field) {
            // Show a dimmed down arrow for columns that aren't currently sorted
            return _jsx(CatIconChevronDown, { className: "opacity-30" }, `${field}-inactive`);
        }
        // For the active sorted column:
        // DESC = up arrow (rotate 180 degrees), ASC = down arrow (normal)
        return orderBy === "DESC" ? (_jsx(CatIconChevronDown, { className: "transform rotate-180 text-blue-600" }, `${field}-desc`)) : (_jsx(CatIconChevronDown, { className: "text-blue-600" }, `${field}-asc`));
    }, [sortBy, orderBy]);
    const renderEmptyState = () => (_jsx("tr", { children: _jsx("td", { className: "py-10 text-center", colSpan: TABLE_COL_SPAN, "data-test": "shared-links-empty", children: _jsxs("div", { className: "flex flex-col justify-center items-center h-64 py-8", children: [_jsx("img", { alt: emptyMessage, className: "w-48 h-48 mb-4", src: ErrorChargingOption }), _jsx("p", { className: `text-gray-500 text-lg ${emptyMessage === DEFAULT_EMPTY_MESSAGE ? "font-medium" : "font-['Noto_Sans']"}`, children: emptyMessage })] }) }) }));
    const populateSharableLinkableData = () => {
        // BAB units have no shareable links: always render the empty "Not Applicable"
        // state, regardless of any cached/returned links, so no rows/actions show.
        if (forceEmpty) {
            return renderEmptyState();
        }
        if (isLoading) {
            return (_jsx("tr", { children: _jsx("td", { className: "py-20 text-center", colSpan: TABLE_COL_SPAN, "data-test": "shared-links-loading", children: _jsx("div", { className: "flex justify-center items-center", children: _jsx(CatLoadingIndicator, {}) }) }) }));
        }
        if (isError) {
            return (_jsx("tr", { children: _jsx("td", { className: "py-10 text-center", colSpan: TABLE_COL_SPAN, "data-test": "shared-links-error", children: _jsx(APIError, { refreshHandler: refetch }) }) }));
        }
        // Show loader when fetching new page data (isFetching but not initial loading)
        if (isFetching && !isLoading) {
            return (_jsx("tr", { children: _jsx("td", { className: "py-20 text-center", colSpan: TABLE_COL_SPAN, "data-test": "shared-links-page-loading", children: _jsx("div", { className: "flex justify-center items-center", children: _jsx(CatLoadingIndicator, {}) }) }) }));
        }
        if (links.length === 0) {
            return renderEmptyState();
        }
        return (_jsx(_Fragment, { children: links.map((shareableLink, index) => {
                const rowNumber = (currentPage - 1) * pageSize + (index + 1);
                return (_jsxs("tr", { className: "border-b pb-2 text-xs sm:text-sm hover:bg-gray-50", "data-test": `shared-link-row-${index}`, children: [_jsx("td", { className: "py-3 px-2", "data-test": `shared-link-number-${index}`, children: rowNumber }), _jsx("td", { className: "py-3 px-2", children: _jsx("span", { className: "block truncate max-w-[150px] sm:max-w-[200px]", "data-test": `shared-link-name-${index}`, title: shareableLink.name, children: shareableLink.name }) }), _jsx("td", { className: "py-3 px-2", children: _jsx(CatStatusIndicator, { "data-test": `shared-link-status-${index}`, text: formatStatusForDisplay(shareableLink.status), variant: getStatusVariant(shareableLink.status) }) }), _jsx("td", { className: "py-3 px-2 whitespace-nowrap", "data-test": `shared-link-expire-date-${index}`, children: shareableLink.expiryDate }), _jsx("td", { className: "py-3 px-2", "data-test": `shared-link-features-${index}`, children: _jsxs("div", { className: "flex gap-1 flex-wrap", children: [shareableLink.features?.liveViewOnly && (_jsxs(CatTooltip, { position: "top", children: [_jsx("span", { slot: "trigger", children: _jsx(CatIconShow, {}) }), _jsxs("span", { children: [_jsx("div", { children: _jsx("b", { children: "Live View Only" }) }), _jsx("div", { children: "Can be used without CAT Login" })] })] })), shareableLink.features?.canSeeHistory && (_jsxs(CatTooltip, { position: "top", children: [_jsx("span", { slot: "trigger", children: _jsx(CatIconSyncronize, {}) }), _jsx("span", { children: "Can see history" })] })), shareableLink.features?.canUseChargerControls && (_jsxs(CatTooltip, { position: "top", children: [_jsx("span", { slot: "trigger", children: _jsx("img", { alt: "charger controls", className: "w-3 h-3 sm:w-4 sm:h-4", src: Frame }) }), _jsx("span", { children: "Can use charger controls" })] })), shareableLink.features?.canUseSchedule && (_jsxs(CatTooltip, { position: "top", children: [_jsx("span", { slot: "trigger", children: _jsx(CatIconCalendarClock, {}) }), _jsx("span", { children: "Can use schedule" })] }))] }) }), _jsx("td", { className: "py-3 px-2 text-center", "data-test": `shared-link-views-${index}`, children: shareableLink.views }), isCatAdminUserEnergyFlowLinks(userEntitlements) && (_jsx("td", { className: "py-3 px-2", children: _jsx("button", { "aria-label": "Copy link", className: "p-1 hover:bg-gray-200 rounded transition-colors", "data-test": `shared-link-copy-button-${index}`, onClick: () => handleCopyLink(shareableLink.link), title: shareableLink.link, type: "button", children: _jsx(CatIconCopy, {}) }) })), _jsx("td", { className: "py-3 px-2", children: _jsx("div", { className: "relative", children: isCatAdminUserEnergyFlowLinks(userEntitlements) && (_jsxs(CatContextualMenu, { "data-test": `shared-link-menu-${index}`, position: "bottom-end", children: [_jsx("div", { slot: "trigger", children: _jsx("button", { "aria-label": "More options", className: "p-1 hover:bg-gray-200 rounded transition-colors", "data-test": `shared-link-menu-trigger-${index}`, type: "button", children: _jsx(CatIconEllipsis, {}) }) }), _jsxs(CatList, { className: "w-40", children: [_jsx(CatListItem, { className: "outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 border-none", "data-test": `shared-link-edit-option-${index}`, disabled: shareableLink.status.toLowerCase() === "expired" ||
                                                        shareableLink.status.toLowerCase() === "deactivated", onBlSelect: () => handleEditLink(shareableLink), children: _jsxs("div", { className: `flex items-center outline-none focus:outline-none ${shareableLink.status.toLowerCase() === "expired" ||
                                                            shareableLink.status.toLowerCase() === "deactivated"
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : ""}`, children: [_jsx("span", { className: "mr-3", children: _jsx(CatIconEdit, {}) }), "Edit"] }) }), _jsx(CatListItem, { "data-test": `shared-link-deactivate-option-${index}`, disabled: shareableLink.status.toLowerCase() === "expired" ||
                                                        shareableLink.status.toLowerCase() === "deactivated", onBlSelect: () => handleDeactivateLink(shareableLink), children: _jsxs("div", { className: `flex items-center ${shareableLink.status.toLowerCase() === "expired" ||
                                                            shareableLink.status.toLowerCase() === "deactivated"
                                                            ? "opacity-50 cursor-not-allowed text-gray-400"
                                                            : "text-[#990000]"}`, children: [_jsx("span", { className: "mr-3", children: _jsx(CatIconWarningTriangleFilled, {}) }), "Deactivate"] }) })] })] })) }) })] }, shareableLink.link));
            }) }));
    };
    return (_jsxs("div", { className: "w-full overflow-x-auto xl:overflow-x-visible", "data-test": "shared-list-container", children: [_jsx(SharedLinkToast, { heading: "Link copied successfully", message: _jsxs(_Fragment, { children: ["Link copied! Please share this link in a secure way as anybody with this link can", _jsx("br", {}), "access the data."] }), onClose: () => setShowCopyToast(false), show: showCopyToast }), _jsx(SharedLinkToast, { heading: "Link edited successfully", message: "Link updated successfully. Your changes have been saved.", onClose: () => setShowEditToast(false), show: showEditToast }), _jsx(SharedLinkToast, { heading: "Link successfully deactivated", message: "The Link is not accessible anymore. If you wish to share the data, please create new link.", onClose: () => setShowDeactivateToast(false), show: showDeactivateToast }), _jsx("div", { className: "min-w-[800px] xl:min-w-0", children: _jsxs("table", { className: "table-auto w-full ml-3 text-left", "data-test": "shared-links-table", children: [_jsx("thead", { className: "py-4", children: _jsxs("tr", { className: "border-b", children: [_jsx("th", { className: "py-3 px-2", "data-test": "shared-links-header-number", children: _jsx("div", { className: "flex items-center", children: _jsx("span", { className: "text-xs sm:text-sm", children: "#" }) }) }), _jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-name", children: _jsxs("button", { className: "flex items-center hover:text-blue-600 transition-colors cursor-pointer", "data-test": "shared-links-sort-name", onClick: () => handleSort("name"), type: "button", children: [_jsx("span", { className: "text-xs sm:text-sm", children: "Name" }), _jsx("span", { className: "ml-2 mt-1", children: getSortIcon("name") })] }) }), _jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-status", children: _jsxs("button", { className: "flex items-center hover:text-blue-600 transition-colors cursor-pointer", "data-test": "shared-links-sort-status", onClick: () => handleSort("status"), type: "button", children: [_jsx("span", { className: "text-xs sm:text-sm", children: "Status" }), _jsx("span", { className: "ml-2 mt-1", children: getSortIcon("status") })] }) }), _jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-expiry", children: _jsxs("button", { className: "flex items-center hover:text-blue-600 transition-colors cursor-pointer", "data-test": "shared-links-sort-expiry", onClick: () => handleSort("expiryDate"), type: "button", children: [_jsx("span", { className: "text-xs sm:text-sm whitespace-nowrap", children: "Expire Date" }), _jsx("span", { className: "ml-2 mt-1", children: getSortIcon("expiryDate") })] }) }), _jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-features", children: _jsxs("button", { className: "flex items-center hover:text-blue-600 transition-colors cursor-pointer", "data-test": "shared-links-sort-features", onClick: () => handleSort("features"), type: "button", children: [_jsx("span", { className: "text-xs sm:text-sm", children: "Features" }), _jsx("span", { className: "ml-2 mt-1", children: getSortIcon("features") })] }) }), _jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-views", children: _jsxs("button", { className: "flex items-center hover:text-blue-600 transition-colors cursor-pointer", "data-test": "shared-links-sort-views", onClick: () => handleSort("NumberOfViews"), type: "button", children: [_jsx("span", { className: "text-xs sm:text-sm whitespace-nowrap", children: "Number of views" }), _jsx("span", { className: "ml-2 mt-1", children: getSortIcon("NumberOfViews") })] }) }), isCatAdminUserEnergyFlowLinks(userEntitlements) && (_jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-link", children: _jsx("div", { className: "flex items-center", children: _jsx("span", { className: "text-xs sm:text-sm", children: "Link" }) }) })), _jsx("th", { className: "text-left py-3 px-2", "data-test": "shared-links-header-actions" })] }) }), _jsx("tbody", { "data-test": "shared-links-tbody", children: populateSharableLinkableData() })] }) }), !isLoading &&
                !isError &&
                context === CONTEXT_SHARED_LINKS_LIST &&
                (links.length > 0 || currentPage > 1 || !!nextCursor) && (_jsx("div", { className: "mt-4 px-3", "data-test": "shared-links-pagination", children: _jsxs("div", { className: "relative", children: [isFetching && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10", "data-test": "shared-links-pagination-loading", children: _jsx(CatLoadingIndicator, {}) })), _jsx(CatPagination, { currentItem: currentPage, "data-test": "shared-links-cat-pagination", onBlPageChange: onPageChange, pageSize: pageSize, pageSizeOptions: pageSizeOptions, totalRecords: totalRecords })] }) })), _jsx(EditLinkModal, { "data-test": "edit-link-modal", isOpen: !!editingLink, linkData: editingLink, onClose: () => setEditingLink(null), onSave: handleSaveLink }), _jsx(DeactivateLinkModal, { "data-test": "deactivate-link-modal", deactivatingLinkData: deactivatingLink, isOpen: !!deactivatingLink, onClose: () => setDeactivatingLink(null), onConfirm: handleConfirmDeactivate })] }));
};
export default SharedList;
